<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { newTour } from "@/utils/shepherd/main";

// A small card offering the tour to first-time visitors, instead of a pop-up that blocks
// the page. Starting a make without it counts as a no to the welcome tour only; the
// tips on each tab still appear the first time it's opened.
const store = useStore();

const wanted = computed(
  () => store.state.tours.showInitialTour && !store.state.drawer.isOpen
);
const shown = ref(false);
let timer = null;

onMounted(() => {
  // Let the page settle first
  timer = setTimeout(() => (shown.value = wanted.value), 1200);
});
onUnmounted(() => clearTimeout(timer));

watch(
  () => store.state.drawer.isOpen,
  (open) => {
    if (open && shown.value) {
      shown.value = false;
      store.dispatch("tours/completeInitialTour");
      // The first tab's tips take over
      newTour();
    }
  }
);
watch(wanted, (value) => {
  if (!value) shown.value = false;
});

const start = () => {
  shown.value = false;
  newTour(true);
};
const notNow = () => {
  shown.value = false;
  store.dispatch("tours/cancelTours");
};
</script>

<template>
  <!-- At the bottom of the screen, clear of the genie, the heading and the start box.
    On phones it's a card over the ideas and small print under the start box; from
    tablets up the same card lies in one row along the bottom, and on short phones it
    shrinks to a slim row, so it stays clear of the start box's button too -->
  <transition
    enter-active-class="transition duration-300 ease-out"
    enter-from-class="translate-y-4 opacity-0"
    leave-active-class="transition duration-200 ease-in"
    leave-to-class="translate-y-4 opacity-0"
  >
    <aside
      v-if="shown"
      class="fixed inset-x-3 bottom-[max(0.75rem,env(safe-area-inset-bottom))] z-55 mx-auto max-w-sm rounded-2xl border border-gray-200 bg-white p-3 shadow-2xl short:bottom-[max(0.5rem,env(safe-area-inset-bottom))] short:flex short:items-center short:gap-2 short:py-2 short:pr-2 sm:flex sm:max-w-2xl sm:items-center sm:gap-6 sm:pr-4 lg:bottom-3"
      aria-labelledby="tour-welcome-title"
    >
      <div
        class="flex items-start gap-3 short:flex-1 sm:flex-1 sm:items-center"
      >
        <span
          class="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full bg-accentSecondaryLight short:hidden"
          aria-hidden="true"
        >
          <img src="/genie-crossed-torso.svg" alt="" class="mt-2 w-10" />
        </span>
        <div class="min-w-0 flex-1">
          <p
            id="tour-welcome-title"
            class="font-bold text-primary short:text-sm short:leading-tight"
          >
            New to MakeMeA?
          </p>
          <p class="text-sm text-secondary short:hidden">
            Four quick tips on getting a great make. It takes about 30 seconds.
          </p>
          <p class="hidden text-xs text-secondary short:block">
            A 30-second tour
          </p>
        </div>
      </div>
      <div
        class="mt-3 flex items-center justify-end gap-2 short:mt-0 short:shrink-0 short:gap-1 sm:mt-0 sm:shrink-0"
      >
        <button
          type="button"
          class="rounded-xl px-4 py-2 text-sm font-bold text-secondary transition-colors hover:text-primary short:px-3"
          @click="notNow"
        >
          Not now
        </button>
        <button
          type="button"
          class="rounded-xl bg-accentPrimary px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-accentPrimaryLight short:px-3"
          aria-label="Show me round"
          @click="start"
        >
          Show me<span class="short:hidden"> round</span>
        </button>
      </div>
    </aside>
  </transition>
</template>
